import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  House, ChatCircleDots, Stethoscope, CalendarCheck, TestTube,
  Pill, ForkKnife, Warning, ShieldCheck, SignOut, ChartLine, User,
} from "@phosphor-icons/react";

const patientNav = [
  { to: "/app", label: "Home", icon: House, end: true },
  { to: "/app/ai", label: "AI Guide", icon: ChatCircleDots },
  { to: "/app/doctors", label: "Doctors", icon: Stethoscope },
  { to: "/app/appointments", label: "Appointments", icon: CalendarCheck },
  { to: "/app/labs", label: "Lab Reports", icon: TestTube },
  { to: "/app/pharmacy", label: "Pharmacy", icon: Pill },
  { to: "/app/nutrition", label: "Nutrition", icon: ForkKnife },
  { to: "/app/emergency", label: "Emergency", icon: Warning },
  { to: "/app/profile", label: "Profile", icon: User },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = () => { logout(); nav("/"); };

  const isAdmin = user?.role === "admin";

  return (
    <div className="min-h-screen bg-bone flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col bg-ayurveda text-bone border-r border-black/10">
        <div className="p-6 border-b border-white/10">
          <div className="font-heritage text-3xl leading-none">Gutvibe</div>
          <div className="overline text-terracotta mt-1">Health · Food as Medicine</div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {patientNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g,'-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200 ${
                  isActive ? "bg-terracotta text-white" : "text-bone/80 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <item.icon size={20} weight="duotone" aria-hidden="true" />
              <span className="text-sm">{item.label}</span>
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200 mt-6 border-t border-white/10 pt-6 ${
                  isActive ? "bg-terracotta text-white" : "text-bone/80 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <ChartLine size={20} weight="duotone" aria-hidden="true" />
              <span className="text-sm">Admin Console</span>
            </NavLink>
          )}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-terracotta flex items-center justify-center text-white font-medium">
              {(user?.name || "?").slice(0,1)}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-bone/60 capitalize">{user?.role}</div>
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            className="w-full bg-white/10 hover:bg-white/20 text-white border-0"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <SignOut size={16} className="mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile top-bar */}
      <div className="md:hidden fixed top-0 inset-x-0 bg-ayurveda text-bone z-40 flex items-center justify-between px-4 py-3 border-b border-black/10">
        <div className="font-heritage text-2xl leading-none">Gutvibe</div>
        <Button variant="ghost" size="sm" onClick={handleLogout} className="text-bone" data-testid="mobile-logout-btn">
          <SignOut size={18} />
        </Button>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 pt-14 md:pt-0">
        {/* Mission banner — visible on every logged-in page */}
        <div className="bg-terracotta/10 text-terracotta text-[11px] px-4 md:px-12 py-1.5 border-b border-terracotta/20 text-center font-clinical">
          Food as Medicine · ഭക്ഷണം മരുന്ന് · Priya, your 30-year-old Malayali AI health companion
        </div>
        {children}
        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 bg-ayurveda text-bone flex items-center justify-around py-2 border-t border-black/10 z-40">
          {patientNav.slice(0,5).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`mnav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-1 px-2 text-[10px] ${isActive ? "text-terracotta" : "text-bone/70"}`
              }
            >
              <item.icon size={22} weight="duotone" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </main>
    </div>
  );
}
